import React, { useRef, useState } from 'react';
import { Button, Icon, Modal, Text } from './';
import { api } from '../_library';
import { useIntl } from '../_library/intl';

// Card holder first/last name: the pencil edit on a card and the CSV import by
// personal code. A saved name is applied by the backend to every card of the
// same person in the company (closed cards included).

type CardRow = {
    id: number,
    cardLastDigits: string,
    firstName: string,
    lastName: string,
    type: string,
    status: string,
};

type PersonRow = {
    line: number,
    personalCode: string,
    firstName: string,
    lastName: string,
    cards: CardRow[],
};

type Preview = {
    automatic: PersonRow[],
    review: PersonRow[],
    unchangedCount: number,
    notFound: { line: number, personalCode: string, firstName: string, lastName: string }[],
    invalidRows: { line: number, personalCode: string, error: string, field?: string }[],
};

const KNOWN_ERRORS = [
    'name_required', 'name_too_long', 'name_invalid_characters', 'personal_code_required',
    'file_required', 'file_unreadable', 'file_empty', 'file_missing_columns', 'file_too_many_rows',
    'duplicate_personal_codes', 'invalid_rows', 'card_not_found',
];

const errorLabel = (code?: string) =>
    `bk.card_holder_names.error.${code && KNOWN_ERRORS.includes(code) ? code : 'general'}`;

const fullName = (first?: string, last?: string) => `${first || ''} ${last || ''}`.trim();

// ---------------------------------------------------------------------------
// Pencil + modal to edit the name of one card
// ---------------------------------------------------------------------------
type EditProps = {
    url: string,                     // PATCH endpoint of this card's holder name
    firstName: string,
    lastName: string,
    onSaved: (firstName: string, lastName: string) => void,
    iconClassName?: string,
};

export const CardHolderNameEdit = ({url, firstName, lastName, onSaved, iconClassName}: EditProps) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [values, setValues] = useState({firstName, lastName});
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const open = () => {
        setValues({firstName, lastName});
        setError('');
        setIsOpen(true);
    };

    const save = () => {
        if (!values.firstName.trim() || !values.lastName.trim()) {
            setError('name_required');
            return;
        }

        setIsSaving(true);
        setError('');

        api.patch(url, {firstName: values.firstName, lastName: values.lastName})
            .then(res => {
                setIsSaving(false);
                setIsOpen(false);
                onSaved(res.data.firstName, res.data.lastName);
            })
            .catch(e => {
                setIsSaving(false);
                setError(e?.response?.data?.error || 'general');
            });
    };

    return (
        <>
            <span className="inline-flex cursor-pointer ml-2" title={intl.formatMessage({id: 'bk.card_holder_names.edit.title'})}>
                <Icon type="pencil" onClick={open} className={iconClassName || ''} />
            </span>
            <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} title="bk.card_holder_names.edit.title"
                content={
                    <div>
                        <Text left className="text-sm mb-4" label="bk.card_holder_names.edit.description" />
                        {error ? <div className="mb-3"><Text left error label={errorLabel(error)} /></div> : null}
                        {(['firstName', 'lastName'] as const).map(field => (
                            <div key={field} className="mb-3">
                                <label className="block text-sm font-medium mb-1">
                                    {intl.formatMessage({id: `bk.card_holder_names.${field}`})}
                                </label>
                                <input
                                    className="w-full border rounded-md px-3 py-2"
                                    value={values[field]}
                                    maxLength={100}
                                    onChange={e => setValues(prev => ({...prev, [field]: e.target.value}))}
                                />
                            </div>
                        ))}
                        <div className="flex justify-between mt-6">
                            <Button label="bk.card_holder_names.save" isLoading={isSaving} disabled={isSaving} onClick={save} />
                            <Button secondary={true} label="bk.card_holder_names.cancel" disabled={isSaving}
                                    onClick={() => setIsOpen(false)} />
                        </div>
                    </div>
                }
            />
        </>
    );
};

// ---------------------------------------------------------------------------
// Import button + review window
// ---------------------------------------------------------------------------
type ImportProps = {
    previewUrl: string,
    importUrl: string,
    onImported?: () => void,
    disabled?: boolean,
};

const Pill = ({label, tone}: { label: string, tone: 'grey' | 'green' | 'outline' }) => (
    <span className={'inline-block rounded-full px-2 py-0.5 text-xs ' + (
        tone === 'green' ? 'bg-green-100 text-green-800'
            : tone === 'grey' ? 'bg-gray-200 text-gray-700'
                : 'border border-gray-300 text-gray-700')}>
        {label}
    </span>
);

export const CardHolderNamesImport = ({previewUrl, importUrl, onImported, disabled}: ImportProps) => {
    const intl = useIntl();
    const fileInput = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [edited, setEdited] = useState<{[personalCode: string]: {firstName: string, lastName: string}}>({});
    const [error, setError] = useState<{code: string, details?: any} | null>(null);
    const [result, setResult] = useState<{updatedPeople: number, updatedCards: number, notFound: string[]} | null>(null);

    const reset = () => {
        setPreview(null);
        setEdited({});
        setError(null);
        setResult(null);
    };

    const close = () => {
        setIsOpen(false);
        reset();
    };

    const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';  // allow choosing the same file again

        if (!file) {
            return;
        }

        reset();
        setIsLoading(true);
        setIsOpen(true);

        const reader = new FileReader();

        reader.onload = () => {
            api.post(previewUrl, {file: reader.result})
                .then(res => {
                    setIsLoading(false);
                    setPreview(res.data);
                    const initial: {[code: string]: {firstName: string, lastName: string}} = {};
                    (res.data.review as PersonRow[]).forEach(row => {
                        initial[row.personalCode] = {firstName: row.firstName, lastName: row.lastName};
                    });
                    setEdited(initial);
                })
                .catch(err => {
                    setIsLoading(false);
                    setError({code: err?.response?.data?.error || 'general', details: err?.response?.data?.details});
                });
        };

        reader.onerror = () => {
            setIsLoading(false);
            setError({code: 'file_unreadable'});
        };

        reader.readAsDataURL(file);
    };

    const save = () => {
        if (preview === null) {
            return;
        }

        const reviewed = preview.review.map(row => ({personalCode: row.personalCode, ...edited[row.personalCode]}));

        if (reviewed.some(row => !row.firstName.trim() || !row.lastName.trim())) {
            setError({code: 'name_required'});
            return;
        }

        const items = [
            ...preview.automatic.map(row => ({personalCode: row.personalCode, firstName: row.firstName, lastName: row.lastName})),
            ...reviewed,
        ];

        setIsSaving(true);
        setError(null);

        api.post(importUrl, {items})
            .then(res => {
                setIsSaving(false);
                setResult(res.data);
                if (onImported) {
                    onImported();
                }
            })
            .catch(err => {
                setIsSaving(false);
                setError({code: err?.response?.data?.error || 'general', details: err?.response?.data?.details});
            });
    };

    const changes = preview ? preview.automatic.length + preview.review.length : 0;

    const renderError = () => {
        if (error === null) {
            return null;
        }

        const rows = error.details?.rows as Preview['invalidRows'] | undefined;
        const codes = error.details?.personalCodes as string[] | undefined;

        return (
            <div className="mb-4">
                <Text left error label={errorLabel(error.code)} />
                {codes && codes.length ? <Text left className="text-sm mt-1">{codes.join(', ')}</Text> : null}
                {rows && rows.length ?
                    <ul className="text-sm mt-1 list-disc ml-5">
                        {rows.map(row => (
                            <li key={`${row.line}-${row.personalCode}`}>
                                {row.personalCode}: {intl.formatMessage({id: errorLabel(row.error)})}
                            </li>
                        ))}
                    </ul>
                    : null}
            </div>
        );
    };

    const renderSkipped = () => {
        if (preview === null || (!preview.notFound.length && !preview.invalidRows.length)) {
            return null;
        }

        return (
            <div className="mt-4 text-sm">
                {preview.notFound.length ?
                    <details className="mb-2">
                        <summary className="cursor-pointer">
                            {intl.formatMessage({id: 'bk.card_holder_names.import.not_found'}, {count: preview.notFound.length})}
                        </summary>
                        <ul className="list-disc ml-5 mt-1">
                            {preview.notFound.map(row => (
                                <li key={row.personalCode}>{row.personalCode} – {fullName(row.firstName, row.lastName)}</li>
                            ))}
                        </ul>
                    </details>
                    : null}
                {preview.invalidRows.length ?
                    <details>
                        <summary className="cursor-pointer">
                            {intl.formatMessage({id: 'bk.card_holder_names.import.invalid_rows'}, {count: preview.invalidRows.length})}
                        </summary>
                        <ul className="list-disc ml-5 mt-1">
                            {preview.invalidRows.map(row => (
                                <li key={`${row.line}-${row.personalCode}`}>
                                    {intl.formatMessage({id: 'bk.card_holder_names.import.line'}, {line: row.line})}
                                    {row.personalCode ? ` (${row.personalCode})` : ''}: {intl.formatMessage({id: errorLabel(row.error)})}
                                </li>
                            ))}
                        </ul>
                    </details>
                    : null}
            </div>
        );
    };

    const renderReviewTable = () => {
        if (preview === null || !preview.review.length) {
            return null;
        }

        return (
            <div className="max-h-[50vh] overflow-y-auto border rounded-md">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500 text-xs uppercase sticky top-0">
                        <tr>
                            <th className="px-3 py-2 w-2/5">{intl.formatMessage({id: 'bk.card_holder_names.import.new_name'})}</th>
                            <th className="px-3 py-2">{intl.formatMessage({id: 'bk.card_holder_names.import.card_id'})}</th>
                            <th className="px-3 py-2">{intl.formatMessage({id: 'bk.card_holder_names.import.name_on_card'})}</th>
                            <th className="px-3 py-2">{intl.formatMessage({id: 'bk.card_holder_names.import.type'})}</th>
                            <th className="px-3 py-2">{intl.formatMessage({id: 'bk.card_holder_names.import.status'})}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preview.review.map(row => (
                            row.cards.map((card, idx) => (
                                <tr key={`${row.personalCode}-${card.id}`} className={idx === 0 ? 'border-t' : ''}>
                                    {idx === 0 ?
                                        <td className="px-3 py-2 align-top" rowSpan={row.cards.length}>
                                            <div className="flex gap-2">
                                                {(['firstName', 'lastName'] as const).map(field => (
                                                    <input
                                                        key={field}
                                                        className="w-1/2 border rounded-md px-2 py-1"
                                                        placeholder={intl.formatMessage({id: `bk.card_holder_names.${field}`})}
                                                        value={edited[row.personalCode]?.[field] ?? ''}
                                                        maxLength={100}
                                                        onChange={e => setEdited(prev => ({
                                                            ...prev,
                                                            [row.personalCode]: {...prev[row.personalCode], [field]: e.target.value},
                                                        }))}
                                                    />
                                                ))}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">{row.personalCode}</div>
                                        </td>
                                        : null}
                                    <td className="px-3 py-2">
                                        <span className="font-mono text-xs bg-purple-50 text-purple-800 rounded px-1">{card.cardLastDigits}</span>
                                    </td>
                                    <td className={'px-3 py-2 ' + (card.status === 'closed' ? 'text-gray-500' : '')}>
                                        {fullName(card.firstName, card.lastName).toUpperCase()}
                                    </td>
                                    <td className="px-3 py-2">
                                        <Pill tone="outline" label={intl.formatMessage({id: `bk.card_holder_names.card_type.${card.type}`})} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Pill tone={card.status === 'active' ? 'green' : 'grey'}
                                              label={intl.formatMessage({id: `bk.card_holder_names.card_status.${card.status}`})} />
                                    </td>
                                </tr>
                            ))
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return <Text left label="bk.card_holder_names.import.loading" />;
        }

        if (result !== null) {
            return (
                <div>
                    <Text left label="bk.card_holder_names.import.done"
                          values={{people: result.updatedPeople, cards: result.updatedCards}} />
                    <div className="flex justify-end mt-6">
                        <Button label="bk.card_holder_names.close" onClick={close} />
                    </div>
                </div>
            );
        }

        if (preview === null) {
            return (
                <div>
                    {renderError()}
                    <Text left className="text-sm" label="bk.card_holder_names.import.file_hint" />
                    <div className="flex justify-end mt-6">
                        <Button secondary={true} label="bk.card_holder_names.close" onClick={close} />
                    </div>
                </div>
            );
        }

        return (
            <div>
                {renderError()}
                {changes === 0 ?
                    <Text left label="bk.card_holder_names.import.nothing_to_change"
                          values={{unchanged: preview.unchangedCount}} />
                    : preview.review.length ?
                        <Text left className="mb-4" label="bk.card_holder_names.import.review_description"
                              values={{automatic: preview.automatic.length, review: preview.review.length}} />
                        :
                        <Text left className="mb-4" label="bk.card_holder_names.import.confirm_description"
                              values={{automatic: preview.automatic.length}} />
                }
                {renderReviewTable()}
                {renderSkipped()}
                <div className="flex justify-end gap-3 mt-6">
                    <Button secondary={true} label="bk.card_holder_names.cancel" disabled={isSaving} onClick={close} />
                    {changes > 0 ?
                        <Button label="bk.card_holder_names.save" isLoading={isSaving} disabled={isSaving} onClick={save} />
                        : null}
                </div>
            </div>
        );
    };

    return (
        <>
            <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onFileChosen} />
            <Button
                disabled={disabled}
                color="transparent"
                label="bk.card_holder_names.import.button"
                onClick={() => fileInput.current && fileInput.current.click()}
                smallPaddingOnSides={true}
                noShadow={true}
            />
            <Modal isOpen={isOpen} toggle={close} xl={!!preview && preview.review.length > 0} big={!preview || !preview.review.length}
                   title={preview && preview.review.length ? 'bk.card_holder_names.import.review_title' : 'bk.card_holder_names.import.title'}
                   content={renderContent()} />
        </>
    );
};
